import { type FC, type MouseEventHandler, type ReactNode } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy.js';
import classnames from 'classnames';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { CurrencyProvider } from 'dibs-buyer-layout/exports/CurrencyProvider.jsx';
import {
    SharedContactDealerWrapper,
    type ModalType,
} from '../../contactDealer/SharedContactDealerWrapper.tsx';
import { startConversationMessage } from '../pdpMessages.tsx';

// helpers
import { PRICE_TYPES } from 'dibs-buyer-price/src/constants.ts';
import { includes } from 'dibs-ts-utils/exports/includes.ts';

// styles
import styles from './PdpSharedButtons.scss';
import dibsCss from 'dibs-css';

import { type PdpSharedContactDealerButton_item$data } from './__generated__/PdpSharedContactDealerButton_item.graphql.ts';
import { type PdpSharedContactDealerButton_user$data } from './__generated__/PdpSharedContactDealerButton_user.graphql.ts';

type Props = {
    item: PdpSharedContactDealerButton_item$data;
    user: PdpSharedContactDealerButton_user$data | null | undefined;
    size?: 'small' | 'medium' | 'large';
    selectedSkuId?: string;
    source?: string;
    type?:
        | 'CONTACT_DEALER'
        | 'REQUEST_CUSTOMIZATION'
        | 'REQUEST_PRICE'
        | 'REQUEST_IMAGES'
        | 'REQUEST_IMAGES_AND_VIDEOS'
        | 'DESIGN_YOUR_OWN'
        | 'REQUEST_CUSTOMIZATION_ALT';
    fullWidth?: boolean;
    launchOrigin?: string;
    buttonType?: 'primary' | 'secondary' | 'textCta' | 'link' | 'transparent';
    wrapperClassName?: string;
    customCta?: ReactNode;
};

const cannotCustomize = (item: PdpSharedContactDealerButton_item$data): boolean =>
    includes([PRICE_TYPES.UNAVAILABLE, PRICE_TYPES.SOLD], item?.priceType?.[0]?.textType);

export const PdpSharedContactDealerButtonComponent: FC<Props> = ({
    fullWidth = true,
    type = 'CONTACT_DEALER',
    launchOrigin = 'contact link in pdp - sidebar',
    item,
    selectedSkuId,
    size = 'large',
    buttonType = 'primary',
    source,
    user = null,
    wrapperClassName,
    customCta,
}) => {
    const contactTheSellerMessage = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.contactTheSeller"
            defaultMessage="Contact Seller"
        />
    );

    const requestCustomizationMessage = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.requestCustomization"
            defaultMessage="Request Customization"
        />
    );

    const requestPriceMessage = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.requestPrice"
            defaultMessage="Request Price"
        />
    );

    const requestMoreImages = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.requestMoreImages"
            defaultMessage="Request More Images"
        />
    );
    const requestMoreImagesAndVideos = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.requestMore"
            defaultMessage="Request More"
        />
    );
    const designYourOwn = (
        <FormattedMessage
            id="PdpSharedContactDealerButton.designYourOwn"
            defaultMessage="Design Your Own"
        />
    );

    let modalType: ModalType = 'CONTACT_DEALER';
    let message: ReactNode;

    switch (type) {
        case 'REQUEST_PRICE':
            message = requestPriceMessage;
            launchOrigin = 'request price button';
            modalType = type;
            break;
        case 'REQUEST_CUSTOMIZATION':
            message = requestCustomizationMessage;
            launchOrigin = 'contact link in pdp - request customization';
            modalType = type;
            if (cannotCustomize(item)) {
                return null;
            }
            break;
        case 'REQUEST_IMAGES':
            message = requestMoreImages;
            modalType = type;
            break;
        case 'REQUEST_IMAGES_AND_VIDEOS':
            message = requestMoreImagesAndVideos;
            modalType = type;
            break;
        case 'DESIGN_YOUR_OWN':
            message = designYourOwn;
            modalType = type;
            if (cannotCustomize(item)) {
                return null;
            }
            break;
        case 'REQUEST_CUSTOMIZATION_ALT':
            message = requestCustomizationMessage;
            modalType = 'DESIGN_YOUR_OWN';
            if (cannotCustomize(item)) {
                return null;
            }
            break;
        default: {
            if (source === 'shipment-info-link') {
                message = customCta ? customCta : contactTheSellerMessage;
            } else {
                message = startConversationMessage;
            }
            break;
        }
    }

    return (
        <div className={classnames(dibsCss.relative, wrapperClassName)}>
            <CurrencyProvider
                render={({ currency }: { currency: string }) => (
                    <SharedContactDealerWrapper
                        item={item}
                        selectedSkuId={selectedSkuId}
                        launchOrigin={launchOrigin}
                        modalType={modalType}
                        user={user}
                        currency={currency}
                    >
                        {(onClick: MouseEventHandler) =>
                            source === 'shipment-info-link' || buttonType === 'link' ? (
                                <Link
                                    onClick={onClick}
                                    className={classnames(
                                        styles.contactSellerLink,
                                        {
                                            [styles.linkButton]: buttonType === 'link',
                                            [styles.small]:
                                                buttonType === 'link' && size === 'small',
                                        },
                                        styles.printHide
                                    )}
                                    dataTn="item-actions-request-information"
                                >
                                    {message}
                                </Link>
                            ) : (
                                <Button
                                    onClick={onClick}
                                    type={buttonType}
                                    className={classnames(
                                        { [styles.fullWidth]: fullWidth },
                                        styles.printHide
                                    )}
                                    dataTn="item-actions-request-information"
                                    size={size}
                                >
                                    {message}
                                </Button>
                            )
                        }
                    </SharedContactDealerWrapper>
                )}
            />
        </div>
    );
};

export const PdpSharedContactDealerButton = createFragmentContainer(
    PdpSharedContactDealerButtonComponent,
    {
        item: graphql`
            fragment PdpSharedContactDealerButton_item on Item
            @argumentDefinitions(userId: { type: "String", defaultValue: "" }) {
                ...SharedContactDealerWrapper_item
                priceType: displayPrice(
                    isTrade: false
                    buyerId: $userId
                    catchNonFatalErrors: false
                ) {
                    textType
                }
            }
        `,
        user: graphql`
            fragment PdpSharedContactDealerButton_user on User {
                ...SharedContactDealerWrapper_user
            }
        `,
    }
);
